
import { useEffect, useMemo, useState } from "react";
import { Button } from "./button";
import {
    ArrowRight,
} from "lucide-react";
import ContactSalesModal from "../../pages/ContactSalesModal.tsx";

type KAM = {
    id: string;
    name: string;
    country: string; // ISO-2
    available: boolean;
    recommended?: boolean;
    phone?: string; // E.164, e.g. +47...
    headshotSrc?: string;
};

function routeKAMsByCountry(kams: KAM[], userCountry: string): KAM[] {
    // Simple geo routing: exact country match first, else return all.
    const exact = kams.filter((k) => k.country === userCountry);
    return exact.length ? exact : kams;
}

function sortKAMs(kams: KAM[]): KAM[] {
    // Recommended first, then available, then name.
    return [...kams].sort((a, b) => {
        const ar = a.recommended ? 1 : 0;
        const br = b.recommended ? 1 : 0;
        if (ar !== br) return br - ar;

        const aa = a.available ? 1 : 0;
        const ba = b.available ? 1 : 0;
        if (aa !== ba) return ba - aa;

        return a.name.localeCompare(b.name);
    });
}

export default function CampaignBanner({client, baseUrl}) {
    const [openSales, setOpenSales] = useState(false);
    const [showKAM, setShowKAM] = useState(false);

    

    // Close modals on ESC (priority: KAM -> Success -> Demo -> Sales)
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            if (showKAM) return void setShowKAM(false);
            if (openSales) return void setOpenSales(false);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showKAM, openSales]);

    // Mock – replace with geo/IP later
    const userCountry = "NO";

    // Mock data – replace with API later
    const kams: KAM[] = useMemo(
        () => [
            {
                id: "kam_anna",
                name: "Anna Berg",
                country: "NO",
                available: true,
                recommended: true,
                phone: "+4700000000",
                headshotSrc: "/stock/headshot-anna.jpg",
            },
            {
                id: "kam_thomas",
                name: "Thomas Johansen",
                country: "NO",
                available: false,
                phone: "+4700000001",
                headshotSrc: "/stock/headshot-thomas.jpg",
            },
            {
                id: "kam_lars",
                name: "Lars Nilsen",
                country: "SE",
                available: true,
                phone: "+4600000000",
                headshotSrc: "/stock/headshot-lars.jpg",
            },
        ],
        []
    );

    const routedKams = useMemo(
        () => sortKAMs(routeKAMsByCountry(kams, userCountry)),
        [kams, userCountry]
    );

    return (
        <section className="w-full py-16 bg-gradient-to-r from-gray-50 via-white to-gray-100 text-gray-900 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="relative overflow-hidden rounded-3xl shadow-sm">
                    {/* Background image */}
                    <img
                        src="../../wallet-form-side.png"
                        alt="Aktuelle kampanjer"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    {/* Soft overlay for readability */}
                    <div className="absolute inset-0 bg-white/60" />

                    <div className="relative px-5 sm:px-8 py-6 sm:py-8">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                            <div>
                                <div className="text-xs font-semibold tracking-widest text-[var(--primary-color)] mb-2">
                                    ENKEL KUNDEREGISTRERING OG BOOKING MED VIPPS
                                </div>
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                                    lav terskel, høy verdi
                                </h2>
                                <p className="text-gray-700 mt-2 text-sm sm:text-base">
                                    Sett opp Vipps-booking på kort tid: forhåndsautorisert betaling, automatisk faktura og rapportering.
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <a href="/vipps-booking" className="flex items-center rounded-2xl text-white px-5 py-2 shadow transition hover:brightness-90" style={{ backgroundColor: "var(--primary-color)" }}>
                                    SE HVORDAN <ArrowRight className="ml-2 h-4 w-4" />
                                </a>
                                <button onClick={() => setOpenSales(true)} variant="outline" className="flex items-center rounded-2xl text-dark px-5 py-2 shadow transition hover:brightness-90" style={{ backgroundColor: "var(--secondary-color)" }}>
                                    SNAKK MED SALG
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Contact sales modal */}
            <ContactSalesModal
                open={openSales}
                onClose={() => setOpenSales(false)}
                onCallClick={() => {
                    setOpenSales(false);
                    setShowKAM(true);
                }}
                showKAM={showKAM}
                setShowKAM={setShowKAM}
                setOpenSales={setOpenSales}
                client={client}
                baseUrl={baseUrl}
            />
        </section>
    );
}
